import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { Button, Form, Modal } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import {
  faChevronLeft,
  faChevronRight,
  faExternalLinkAlt,
  faSearchPlus,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDebounce } from 'use-debounce';

import {
  BG_1,
  BG_2,
  BORDER_2,
  FG_1,
  FG_2,
  FG_3,
  PRIMARY_BORDER,
  PRIMARY_COLOR,
  PRIMARY_LIGHT_RGBA,
  RADIUS_LG,
  SHADOW_SM,
  SUCCESS_BG,
  SUCCESS_TEXT,
  WHITE,
} from 'components/upb_viewer_beta/css/betaTokens';
import FsaLoader from 'components/shared/FsaLoader';
import {
  getURLWithSearchParams,
  MANUFACTURER_SKU_MAPPING_MANUFACTURERS,
} from 'lib/networking/endpoints';
import { httpGet, httpPost } from 'lib/networking/http';
import { ProductImage } from 'components/brand_sku_mapper_shared/ProductImage';
import SwalDialog from 'lib/utils/toast';
import { uniqueImageUrls } from 'components/brand_sku_mapper_shared/imageHelpers';

// Product-detail (PDP) URL for a catalog (manufacturer) or distributor product; null when the
// global product id is missing (the card then shows no link).
const pdpHref = (globalProductId, productId, type) =>
  globalProductId && productId
    ? `/global-product-detail/${globalProductId}/${productId}/${type}`
    : null;

/**
 * InfluenceMapProductsModal — maps a manufacturer's unmapped distributor products to its catalog.
 *
 * Left panel: the distributor product under review (catalog-style card: image on top, details
 * below, enlarge + product-page links), with a ‹ › navigator through the ``products`` list. Right
 * panel: ranked catalog suggestions as selectable catalog cards (image on top, confidence badge,
 * enlarge + product-page links) plus a live search; selecting one and confirming links the
 * distributor product into that catalog item. ``onMapped`` fires after each successful map.
 *
 * No unit tests: the FSA architecture does not unit-test React components (deliberate
 * no-frontend-tests policy); the mapping logic is covered by the service-layer tests behind the
 * suggestions/map endpoints.
 */
function InfluenceMapProductsModal({
  show,
  onHide,
  manufacturerId,
  products,
  startIndex,
  onMapped,
}) {
  const [index, setIndex] = useState(startIndex || 0);
  const [mappedIds, setMappedIds] = useState(() => new Set());
  const [query, setQuery] = useState('');
  const [debouncedQuery] = useDebounce(query.trim(), 300);

  const [suggestions, setSuggestions] = useState([]);
  const [mfrCode, setMfrCode] = useState(null);
  const [noMatchMsg, setNoMatchMsg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [saving, setSaving] = useState(false);
  // The side-by-side compare overlay ({ left, right }, each { title, subtitle, images, index };
  // right may be null when there is no catalog product to compare; null = closed).
  const [zoom, setZoom] = useState(null);
  const requestSeq = useRef(0);

  const total = products.length;
  const product = products[index] || null;
  const isMapped = product ? mappedIds.has(product.id) : false;

  // Opening (or jumping to a different start row) resets the navigator and clears any search.
  useEffect(() => {
    if (show) {
      setIndex(startIndex || 0);
      setQuery('');
      setMappedIds(new Set());
    }
  }, [show, startIndex]);

  // A new product (or search) always clears the prior selection.
  useEffect(() => {
    setSelectedId(null);
  }, [index, debouncedQuery]);

  useEffect(() => {
    if (!show || !product) {
      return;
    }
    const params = {};
    if (debouncedQuery) {
      params.search_query = debouncedQuery;
    }
    const seq = requestSeq.current + 1;
    requestSeq.current = seq;
    setLoading(true);
    httpGet(
      getURLWithSearchParams(
        `${MANUFACTURER_SKU_MAPPING_MANUFACTURERS}/${manufacturerId}/products/` +
          `${product.id}/suggestions`,
        params,
      ),
    )
      .then(res => {
        if (seq !== requestSeq.current) {
          return; // a newer request superseded this one
        }
        setSuggestions(res.data.suggestions || []);
        setMfrCode(res.data.distributor_manufacturer_product_code || null);
        setNoMatchMsg(res.data.message || null);
      })
      .catch(() => {
        if (seq === requestSeq.current) {
          setSuggestions([]);
          setNoMatchMsg('Could not load suggestions.');
        }
      })
      .finally(() => {
        if (seq === requestSeq.current) {
          setLoading(false);
        }
      });
  }, [show, product, manufacturerId, debouncedQuery]);

  const goTo = next => {
    if (next >= 0 && next < total) {
      setIndex(next);
    }
  };

  // Advance to the next product that still needs mapping; close when none are left ahead
  // (the parent refetches the unmapped list on close, so mapped rows drop off).
  const advance = justMappedId => {
    const done = new Set(mappedIds);
    if (justMappedId != null) {
      done.add(justMappedId);
    }
    for (let i = index + 1; i < total; i += 1) {
      if (!done.has(products[i].id)) {
        setIndex(i);
        return;
      }
    }
    onHide();
  };

  const onMap = () => {
    if (!product || selectedId == null || isMapped) {
      return; // already mapped this session -> don't re-submit
    }
    setSaving(true);
    httpPost(
      `${MANUFACTURER_SKU_MAPPING_MANUFACTURERS}/${manufacturerId}/products/${product.id}/map`,
      { mfr_product_id: selectedId },
    )
      .then(() => {
        setMappedIds(prev => new Set(prev).add(product.id));
        if (onMapped) {
          onMapped(product.id);
        }
        advance(product.id);
      })
      .catch(err =>
        SwalDialog(
          'error',
          err?.response?.data?.message || 'Could not map this product. Please try again.',
          'Mapping failed',
        ),
      )
      .finally(() => setSaving(false));
  };

  if (!product) {
    return null;
  }

  const distPdp = pdpHref(product.global_product_id, product.id, 'distributor');

  // One side of the compare overlay: a product's full image set (thumbnail first, deduped).
  const zoomPanelFor = (title, subtitle, thumbnailUrl, imageUrls) => ({
    title,
    subtitle,
    images: uniqueImageUrls([thumbnailUrl, ...(imageUrls || [])]),
    index: 0,
  });

  // Open the compare overlay: distributor product on the left, a catalog product on the right,
  // each with its own carousel, so the two SKUs' images can be checked side by side. ``mfr`` is
  // the catalog suggestion to compare against (null -> left-only, with a prompt on the right).
  const openCompare = mfr => {
    const left = zoomPanelFor(
      product.name,
      product.distributor_name,
      product.thumbnail_url,
      product.image_urls,
    );
    const right = mfr
      ? zoomPanelFor(mfr.name, mfr.brand_name, mfr.thumbnail_url, mfr.image_urls)
      : null;
    if (left.images.length || (right && right.images.length)) {
      setZoom({ left, right });
    }
  };

  // The manufacturer suggestion to pair with the distributor product when its own enlarge is
  // clicked: the selected one, else the top suggestion. Null while suggestions are (re)loading so
  // the distributor card never pairs with the previous product's stale suggestion.
  const compareTarget = () =>
    loading
      ? null
      : suggestions.find(s => s.mfr_product_id === selectedId) || suggestions[0] || null;

  // Move / jump within one side's carousel (side = 'left' | 'right'), wrapping at the ends.
  const stepZoom = (side, delta) =>
    setZoom(z => {
      const panel = z && z[side];
      if (!panel || panel.images.length < 2) {
        return z;
      }
      const next = (panel.index + delta + panel.images.length) % panel.images.length;
      return { ...z, [side]: { ...panel, index: next } };
    });
  const jumpZoom = (side, i) =>
    setZoom(z => (z && z[side] ? { ...z, [side]: { ...z[side], index: i } } : z));

  // The enlarge + open-product-page icon rail overlaid on a product image. Enlarge shows whenever
  // there is any image (thumbnail or alternates); ``onEnlarge`` opens the side-by-side compare.
  const imageIcons = (thumbnailUrl, imageUrls, href, onEnlarge) => (
    <div className={css(styles.iconRail)}>
      {(thumbnailUrl || (imageUrls && imageUrls.length > 0)) && (
        <button
          type="button"
          className={css(styles.iconBtn)}
          title="Compare images"
          aria-label="Compare images side by side"
          onClick={e => {
            e.stopPropagation();
            onEnlarge();
          }}
        >
          <FontAwesomeIcon icon={faSearchPlus} />
        </button>
      )}
      {href && (
        <a
          className={css(styles.iconBtn)}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          title="Open product page"
          aria-label="Open product page"
          onClick={e => e.stopPropagation()}
        >
          <FontAwesomeIcon icon={faExternalLinkAlt} />
        </a>
      )}
    </div>
  );

  return (
    <>
      <Modal show={show} onHide={onHide} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title className={css(styles.modalTitle)}>Map Products</Modal.Title>
        </Modal.Header>
        <Modal.Body className={css(styles.body)}>
          <div className={css(styles.left)}>
            <div className={css(styles.navRow)}>
              <Button
                variant="link"
                className={css(styles.navBtn)}
                disabled={index === 0 || saving}
                onClick={() => goTo(index - 1)}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </Button>
              <span className={css(styles.navLabel)}>
                Product {index + 1}/{total}
              </span>
              <Button
                variant="link"
                className={css(styles.navBtn)}
                disabled={index + 1 >= total || saving}
                onClick={() => goTo(index + 1)}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </Button>
            </div>

            <div className={css(styles.distCard)}>
              <div className={css(styles.distImageWrap)}>
                <ProductImage
                  src={product.thumbnail_url}
                  imgClassName={css(styles.distThumb)}
                  placeholderClassName={css(styles.distThumbFallback)}
                  placeholderText={(product.brand_name || product.name || '?').charAt(0)}
                />
                {imageIcons(product.thumbnail_url, product.image_urls, distPdp, () =>
                  openCompare(compareTarget()),
                )}
              </div>
              {distPdp ? (
                <a
                  className={css(styles.distName, styles.linkName)}
                  href={distPdp}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {product.name || '—'}
                </a>
              ) : (
                <div className={css(styles.distName)}>{product.name || '—'}</div>
              )}
              <dl className={css(styles.distMeta)}>
                <div className={css(styles.metaRow)}>
                  <dt className={css(styles.metaKey)}>Brand</dt>
                  <dd className={css(styles.metaVal)}>{product.brand_name || '—'}</dd>
                </div>
                <div className={css(styles.metaRow)}>
                  <dt className={css(styles.metaKey)}>Distributor</dt>
                  <dd className={css(styles.metaVal)}>{product.distributor_name || '—'}</dd>
                </div>
                <div className={css(styles.metaRow)}>
                  <dt className={css(styles.metaKey)}>Pack</dt>
                  <dd className={css(styles.metaVal)}>{product.pack_size || '—'}</dd>
                </div>
                <div className={css(styles.metaRow)}>
                  <dt className={css(styles.metaKey)}>SKU</dt>
                  <dd className={css(styles.metaVal)}>
                    {distPdp ? (
                      <a
                        className={css(styles.linkName)}
                        href={distPdp}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {product.vendor_sku || '—'}
                      </a>
                    ) : (
                      product.vendor_sku || '—'
                    )}
                  </dd>
                </div>
                <div className={css(styles.metaRow)}>
                  <dt className={css(styles.metaKey)}>GTIN</dt>
                  <dd className={css(styles.metaVal)}>{product.gtin || '—'}</dd>
                </div>
                {mfrCode && (
                  <div className={css(styles.metaRow)}>
                    <dt className={css(styles.metaKey)}>Mfr Product Code</dt>
                    <dd className={css(styles.metaVal)}>{mfrCode}</dd>
                  </div>
                )}
              </dl>
              {isMapped && <div className={css(styles.mappedTag)}>✓ Mapped</div>}
            </div>
          </div>

          <div className={css(styles.right)}>
            <div className={css(styles.rightHead)}>
              <div>
                <div className={css(styles.rightTitle)}>
                  Map distributor products to your catalog items
                </div>
                <div className={css(styles.rightSub)}>
                  {loading
                    ? 'Searching…'
                    : `${suggestions.length} ${suggestions.length === 1 ? 'result' : 'results'}`}
                </div>
              </div>
              <Form.Control
                className={css(styles.search)}
                type="search"
                placeholder="Search name, SKU, GTIN"
                value={query}
                onChange={e => setQuery(e.target.value)}
              />
            </div>

            {!debouncedQuery && <div className={css(styles.suggHeading)}>Top Suggestions</div>}

            {loading ? (
              <div className={css(styles.loadingWrap)}>
                <FsaLoader size={48} label="Loading suggestions…" />
              </div>
            ) : suggestions.length === 0 ? (
              <div className={css(styles.empty)}>
                {noMatchMsg || 'No catalog matches. Try searching by name, SKU, or GTIN.'}
              </div>
            ) : (
              <div className={css(styles.suggList)}>
                {suggestions.map(s => {
                  const selected = selectedId === s.mfr_product_id;
                  const sPdp = pdpHref(s.global_product_id, s.mfr_product_id, 'manufacturer');
                  return (
                    <div
                      key={s.mfr_product_id}
                      role="button"
                      tabIndex={0}
                      className={css(styles.suggCard, selected && styles.suggCardSelected)}
                      onClick={() => setSelectedId(s.mfr_product_id)}
                      onKeyDown={e => {
                        // Only select on keys landing on the card itself -- let Enter on a
                        // nested PDP link / enlarge button fire its own action.
                        if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
                          e.preventDefault();
                          setSelectedId(s.mfr_product_id);
                        }
                      }}
                    >
                      {s.confidence === 100 ? (
                        <span className={css(styles.matchBadge)}>100% Match</span>
                      ) : (
                        s.confidence != null && (
                          <span className={css(styles.confPill)}>{s.confidence}% match</span>
                        )
                      )}
                      <span className={css(styles.radio, selected && styles.radioOn)} />
                      <div className={css(styles.suggImageWrap)}>
                        <ProductImage
                          src={s.thumbnail_url}
                          imgClassName={css(styles.suggThumb)}
                          placeholderClassName={css(styles.suggThumbFallback)}
                          placeholderText={(s.brand_name || s.name || '?').charAt(0)}
                        />
                        {imageIcons(s.thumbnail_url, s.image_urls, sPdp, () => openCompare(s))}
                      </div>
                      {sPdp ? (
                        <a
                          className={css(styles.suggName, styles.linkName)}
                          href={sPdp}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={e => e.stopPropagation()}
                        >
                          {s.name || '—'}
                        </a>
                      ) : (
                        <div className={css(styles.suggName)}>{s.name || '—'}</div>
                      )}
                      <div className={css(styles.suggMeta)}>{s.brand_name || '—'}</div>
                      <div className={css(styles.suggMeta)}>{s.pack_size || '—'}</div>
                      <div className={css(styles.suggMeta)}>
                        SKU:{' '}
                        {sPdp ? (
                          <a
                            className={css(styles.linkName)}
                            href={sPdp}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={e => e.stopPropagation()}
                          >
                            {s.manufacturer_sku || '—'}
                          </a>
                        ) : (
                          s.manufacturer_sku || '—'
                        )}
                      </div>
                      <div className={css(styles.suggMeta)}>GTIN: {s.gtin || '—'}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer className={css(styles.footer)}>
          <Button variant="outline-secondary" disabled={saving} onClick={() => advance(null)}>
            Skip
          </Button>
          <Button
            variant="primary"
            disabled={selectedId == null || saving || isMapped}
            onClick={onMap}
          >
            {saving
              ? 'Mapping…'
              : isMapped
              ? 'Mapped ✓'
              : index + 1 >= total
              ? 'Map & Done'
              : 'Map & Next →'}
          </Button>
        </Modal.Footer>
      </Modal>

      {zoom && (
        <Modal show onHide={() => setZoom(null)} centered dialogClassName={css(styles.zoomDialog)}>
          <Modal.Body className={css(styles.zoomBody)}>
            <button
              type="button"
              className={css(styles.zoomClose)}
              aria-label="Close"
              onClick={() => setZoom(null)}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
            <div className={css(styles.zoomCompareTitle)}>Compare images</div>
            <div className={css(styles.zoomCompare)}>
              {[
                { side: 'left', kind: 'Distributor', panel: zoom.left, catalog: false },
                { side: 'right', kind: 'Manufacturer', panel: zoom.right, catalog: true },
              ].map(({ side, kind, panel, catalog }, idx) => (
                <React.Fragment key={side}>
                  {idx === 1 && zoom.right && (
                    <div className={css(styles.zoomVs)} aria-hidden="true">
                      vs
                    </div>
                  )}
                  {panel ? (
                    <div className={css(styles.zoomPanel)}>
                      <div className={css(styles.zoomPanelHead)}>
                        <span className={css(styles.zoomKind, catalog && styles.zoomKindCatalog)}>
                          {kind}
                        </span>
                        <span className={css(styles.zoomPanelTitle)}>{panel.title || '—'}</span>
                        {panel.subtitle && (
                          <span className={css(styles.zoomPanelSub)}>{panel.subtitle}</span>
                        )}
                      </div>
                      <div className={css(styles.zoomMain)}>
                        {panel.images.length > 1 && (
                          <button
                            type="button"
                            className={css(styles.zoomNav, styles.zoomNavLeft)}
                            aria-label={`${kind}: previous image`}
                            onClick={() => stepZoom(side, -1)}
                          >
                            <FontAwesomeIcon icon={faChevronLeft} />
                          </button>
                        )}
                        <ProductImage
                          src={panel.images[panel.index]}
                          imgClassName={css(styles.zoomImage)}
                          placeholderClassName={css(styles.zoomImagePlaceholder)}
                          placeholderText={(panel.title || '?').charAt(0)}
                        />
                        {panel.images.length > 1 && (
                          <button
                            type="button"
                            className={css(styles.zoomNav, styles.zoomNavRight)}
                            aria-label={`${kind}: next image`}
                            onClick={() => stepZoom(side, 1)}
                          >
                            <FontAwesomeIcon icon={faChevronRight} />
                          </button>
                        )}
                      </div>
                      <div className={css(styles.zoomCounter)}>
                        {panel.images.length
                          ? `${panel.index + 1} / ${panel.images.length}`
                          : 'No images'}
                      </div>
                      {panel.images.length > 1 && (
                        <div className={css(styles.zoomStrip)}>
                          {panel.images.map((url, i) => (
                            <button
                              key={url}
                              type="button"
                              className={css(
                                styles.zoomThumb,
                                i === panel.index && styles.zoomThumbActive,
                              )}
                              aria-label={`${kind} image ${i + 1}`}
                              onClick={() => jumpZoom(side, i)}
                            >
                              <ProductImage
                                src={url}
                                imgClassName={css(styles.zoomThumbImg)}
                                placeholderClassName={css(styles.zoomThumbImg)}
                              />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className={css(styles.zoomPanel, styles.zoomEmptyPanel)}>
                      {loading
                        ? 'Loading suggestions…'
                        : 'No manufacturer product to compare for this distributor SKU.'}
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </Modal.Body>
        </Modal>
      )}
    </>
  );
}

InfluenceMapProductsModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  manufacturerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  products: PropTypes.arrayOf(PropTypes.object).isRequired,
  startIndex: PropTypes.number,
  onMapped: PropTypes.func,
};

InfluenceMapProductsModal.defaultProps = {
  startIndex: 0,
  onMapped: null,
};

const IMAGE_BOX = {
  width: '100%',
  objectFit: 'contain',
  background: WHITE,
  borderRadius: 8,
  border: `1px solid ${BORDER_2}`,
};

const styles = StyleSheet.create({
  modalTitle: { fontSize: 18, fontWeight: 700, color: FG_1 },
  body: { display: 'flex', gap: 20, padding: 20, background: BG_2 },
  left: { width: 300, flexShrink: 0 },
  navRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    border: `1px solid ${BORDER_2}`,
    borderRadius: RADIUS_LG,
    background: BG_1,
    marginBottom: 12,
  },
  navBtn: {
    color: FG_2,
    padding: '6px 14px',
    textDecoration: 'none',
    ':hover': { color: PRIMARY_COLOR },
    ':disabled': { color: FG_3, opacity: 0.5 },
  },
  navLabel: { fontSize: 13, fontWeight: 600, color: FG_1 },
  distCard: {
    border: `1px solid ${BORDER_2}`,
    borderRadius: RADIUS_LG,
    background: BG_1,
    padding: 16,
  },
  // Overlaid enlarge + open-page icons, shared by the left panel and suggestion cards.
  iconRail: { position: 'absolute', top: 6, right: 6, display: 'flex', gap: 6 },
  iconBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 26,
    height: 26,
    borderRadius: '50%',
    border: `1px solid ${BORDER_2}`,
    background: WHITE,
    color: FG_2,
    fontSize: 12,
    cursor: 'pointer',
    textDecoration: 'none',
    boxShadow: SHADOW_SM,
    ':hover': { color: PRIMARY_COLOR, borderColor: PRIMARY_BORDER, background: PRIMARY_LIGHT_RGBA },
  },
  distImageWrap: { position: 'relative', marginBottom: 12 },
  distThumb: { ...IMAGE_BOX, height: 160 },
  distThumbFallback: {
    ...IMAGE_BOX,
    height: 160,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 48,
    fontWeight: 700,
    color: FG_3,
  },
  distName: { fontSize: 15, fontWeight: 700, color: FG_1, marginBottom: 12, display: 'block' },
  linkName: {
    color: PRIMARY_COLOR,
    textDecoration: 'none',
    ':hover': { color: PRIMARY_COLOR, textDecoration: 'underline' },
  },
  distMeta: { margin: 0 },
  metaRow: { display: 'flex', justifyContent: 'space-between', gap: 10, marginBottom: 6 },
  metaKey: { fontSize: 12, color: FG_3, margin: 0, fontWeight: 600 },
  metaVal: {
    fontSize: 12,
    color: FG_1,
    margin: 0,
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
    wordBreak: 'break-word',
  },
  mappedTag: {
    marginTop: 12,
    padding: '6px 10px',
    borderRadius: 6,
    background: SUCCESS_BG,
    color: SUCCESS_TEXT,
    fontSize: 13,
    fontWeight: 700,
    textAlign: 'center',
  },
  right: { flex: 1, minWidth: 0 },
  rightHead: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 16,
    marginBottom: 14,
  },
  rightTitle: { fontSize: 15, fontWeight: 700, color: FG_1 },
  rightSub: { fontSize: 13, color: FG_2, marginTop: 2 },
  search: { maxWidth: 280, fontSize: 13 },
  suggHeading: {
    fontSize: 11,
    fontWeight: 700,
    color: FG_3,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    marginBottom: 10,
  },
  loadingWrap: { padding: '40px 0' },
  empty: {
    padding: '32px 16px',
    textAlign: 'center',
    color: FG_2,
    fontSize: 14,
    border: `1px dashed ${BORDER_2}`,
    borderRadius: RADIUS_LG,
  },
  suggList: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
    gap: 12,
    maxHeight: 460,
    overflowY: 'auto',
    padding: 2,
  },
  suggCard: {
    position: 'relative',
    textAlign: 'left',
    border: `1px solid ${BORDER_2}`,
    borderRadius: RADIUS_LG,
    background: BG_1,
    padding: 12,
    paddingTop: 34,
    cursor: 'pointer',
    outline: 'none',
    ':hover': { borderColor: PRIMARY_BORDER, boxShadow: SHADOW_SM },
    ':focus-visible': { borderColor: PRIMARY_COLOR },
  },
  suggCardSelected: {
    borderColor: PRIMARY_COLOR,
    boxShadow: `0 0 0 2px ${PRIMARY_COLOR}`,
    background: PRIMARY_LIGHT_RGBA,
  },
  matchBadge: {
    position: 'absolute',
    top: 8,
    left: 8,
    fontSize: 11,
    fontWeight: 700,
    color: SUCCESS_TEXT,
    background: SUCCESS_BG,
    borderRadius: 4,
    padding: '2px 8px',
  },
  confPill: {
    position: 'absolute',
    top: 8,
    left: 8,
    fontSize: 11,
    fontWeight: 700,
    color: PRIMARY_COLOR,
    background: PRIMARY_LIGHT_RGBA,
    borderRadius: 4,
    padding: '2px 8px',
  },
  suggImageWrap: { position: 'relative', marginBottom: 10 },
  suggThumb: { ...IMAGE_BOX, height: 120 },
  suggThumbFallback: {
    ...IMAGE_BOX,
    height: 120,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 32,
    fontWeight: 700,
    color: FG_3,
  },
  suggName: {
    fontSize: 13,
    fontWeight: 700,
    color: FG_1,
    marginBottom: 6,
    display: 'block',
    lineHeight: 1.3,
  },
  suggMeta: { fontSize: 12, color: FG_2, fontVariantNumeric: 'tabular-nums', marginBottom: 2 },
  radio: {
    position: 'absolute',
    top: 10,
    right: 10,
    width: 18,
    height: 18,
    borderRadius: '50%',
    border: `2px solid ${BORDER_2}`,
    background: WHITE,
  },
  radioOn: {
    borderColor: PRIMARY_COLOR,
    background: PRIMARY_COLOR,
    boxShadow: `inset 0 0 0 3px ${WHITE}`,
  },
  footer: { background: BG_2 },
  zoomDialog: { maxWidth: 1000 },
  zoomBody: { position: 'relative', padding: 20, background: WHITE },
  zoomCompareTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: FG_2,
    textAlign: 'center',
    marginBottom: 14,
    letterSpacing: 0.3,
    textTransform: 'uppercase',
  },
  zoomCompare: { display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' },
  zoomPanel: { flex: '1 1 0', minWidth: 280 },
  zoomVs: {
    alignSelf: 'center',
    fontSize: 12,
    fontWeight: 700,
    color: FG_3,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  zoomPanelHead: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 8,
    marginBottom: 10,
    flexWrap: 'wrap',
    minHeight: 22,
  },
  zoomKind: {
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    color: PRIMARY_COLOR,
    background: PRIMARY_LIGHT_RGBA,
    borderRadius: 6,
    padding: '2px 7px',
    flexShrink: 0,
  },
  zoomKindCatalog: { color: SUCCESS_TEXT, background: SUCCESS_BG },
  zoomPanelTitle: { fontSize: 14, fontWeight: 600, color: FG_1 },
  zoomPanelSub: { fontSize: 12, color: FG_3 },
  zoomEmptyPanel: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 360,
    color: FG_3,
    fontSize: 13,
    textAlign: 'center',
    border: `1px dashed ${BORDER_2}`,
    borderRadius: 8,
    padding: 16,
  },
  zoomClose: {
    position: 'absolute',
    top: 10,
    right: 10,
    zIndex: 2,
    width: 32,
    height: 32,
    borderRadius: '50%',
    border: `1px solid ${BORDER_2}`,
    background: WHITE,
    color: FG_2,
    cursor: 'pointer',
    ':hover': { color: PRIMARY_COLOR },
  },
  zoomMain: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  zoomImage: { width: '100%', height: 360, objectFit: 'contain', background: WHITE },
  zoomImagePlaceholder: {
    width: '100%',
    height: 360,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: FG_3,
    background: BG_2,
    borderRadius: 8,
  },
  zoomNav: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    width: 36,
    height: 36,
    borderRadius: '50%',
    border: `1px solid ${BORDER_2}`,
    background: WHITE,
    color: FG_1,
    cursor: 'pointer',
    boxShadow: SHADOW_SM,
    ':hover': { color: PRIMARY_COLOR, borderColor: PRIMARY_BORDER },
  },
  zoomNavLeft: { left: 0 },
  zoomNavRight: { right: 0 },
  zoomCounter: {
    textAlign: 'center',
    fontSize: 12,
    fontWeight: 600,
    color: FG_2,
    marginTop: 8,
    fontVariantNumeric: 'tabular-nums',
  },
  zoomStrip: {
    display: 'flex',
    gap: 8,
    overflowX: 'auto',
    marginTop: 12,
    paddingBottom: 4,
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  zoomThumb: {
    flexShrink: 0,
    width: 56,
    height: 56,
    padding: 0,
    borderRadius: 6,
    border: `1px solid ${BORDER_2}`,
    background: WHITE,
    cursor: 'pointer',
    overflow: 'hidden',
    ':hover': { borderColor: PRIMARY_BORDER },
  },
  zoomThumbActive: { borderColor: PRIMARY_COLOR, boxShadow: `0 0 0 2px ${PRIMARY_COLOR}` },
  zoomThumbImg: { width: '100%', height: '100%', objectFit: 'contain', background: WHITE },
});

export default InfluenceMapProductsModal;
